import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  PenNib,
  FileText,
  Sparkle,
  Lightning,
  Eye,
  Download,
  ClockClockwise,
  MusicNote,
  Star,
  PlayCircle,
  CaretLeft,
  CaretRight,
  ArrowRight,
  Quotes,
} from '@phosphor-icons/react';

/**
 * In-page interactive tour for the Welcome page.
 * Steps illustrate the main navigation surfaces of the app using
 * lightweight stylised mocks (NOT real screenshots) so the tour stays
 * accurate even when the UI is iterated.
 */

const Step = ({ icon: Icon, label, title, body, mock }) => (
  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10 items-stretch">
    {/* Text side */}
    <div className="md:col-span-2 flex flex-col justify-center">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg border border-border bg-card flex items-center justify-center">
          <Icon size={20} weight="duotone" className="text-gold" />
        </div>
        <span className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">{label}</span>
      </div>
      <h3 className="font-serif text-2xl md:text-3xl text-ivory leading-tight mb-3">{title}</h3>
      <p className="text-sm text-bone font-sans leading-[1.75]">{body}</p>
    </div>

    {/* Visual / mock side */}
    <div className="md:col-span-3">
      <div className="panel-luxe p-5 md:p-6 h-full">{mock}</div>
    </div>
  </div>
);

const TypePill = ({ active, label }) => (
  <span
    className={`px-3 py-1.5 rounded-full text-xs tracking-wide border ${
      active
        ? 'border-gold/60 bg-gold/10 text-gold'
        : 'border-border bg-card text-ash'
    }`}
  >
    {label}
  </span>
);

const FakeBar = ({ pct, color = 'bg-gold' }) => (
  <div className="h-1.5 rounded-full bg-stone/70 overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
  </div>
);

const STEPS = [
  {
    icon: PlayCircle,
    label: 'Welcome',
    title: 'A quick tour of Manuscript Polish',
    body:
      'Sixty seconds to learn the lay of the land. Use Next ➝ to walk through the workflow, or close this any time — the dashboard itself is built to be obvious.',
    mock: (
      <div className="flex flex-col items-center justify-center h-full py-6">
        <div className="h-14 w-20 rounded-lg border border-gold/40 bg-card/60 flex items-center justify-center mb-4 shadow-[0_8px_32px_rgba(198,168,124,0.08)]">
          <span className="font-serif text-gold text-3xl">MP</span>
        </div>
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">For novelists & screenwriters</p>
        <p className="text-xs text-ash mt-2 text-center max-w-[260px]">
          AI proofreading that respects your voice — and now, an analysis-only mode for songwriters.
        </p>
      </div>
    ),
  },
  {
    icon: FileText,
    label: 'Step 1 · Document type',
    title: 'Pick what you’re working on',
    body:
      'The first dropdown sets the rules. Manuscript and Screenplay get full corrections with diffs. Song mode is analysis-only — your lyrics are kept verbatim and you get a critique of theme, rhyme, and structure.',
    mock: (
      <div className="space-y-4">
        <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">Document Type</p>
        <div className="flex flex-wrap gap-2">
          <TypePill label="Manuscript" active />
          <TypePill label="Screenplay" />
          <TypePill label="Play" />
          <TypePill label="Drama" />
          <TypePill label="Song · Analysis only" />
        </div>
        <div className="text-[11px] text-bone leading-relaxed pt-2">
          <span className="text-gold">Tip:</span> for lyrics, selecting <span className="text-ivory">Song</span> tells the AI to <span className="text-ivory italic">not</span> correct slang or abbreviations.
        </div>
      </div>
    ),
  },
  {
    icon: PenNib,
    label: 'Step 2 · Paste or upload',
    title: 'Paste text — or drop a file',
    body:
      'You can paste straight into the editor, or upload .txt, .docx, or .pdf. Large manuscripts are split into chunks automatically and proofread in sequence with a live progress indicator.',
    mock: (
      <div className="space-y-3">
        <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">Original</div>
        <div className="rounded-md border border-border bg-stone/40 p-3 text-sm text-bone italic leading-relaxed font-serif">
          “She walked quick across the room, opening the door very carefully and seeing him standing their...”
        </div>
        <div className="flex items-center gap-2 text-[11px] text-ash">
          <FileText size={12} weight="duotone" className="text-gold" />
          <span>TXT · DOCX · PDF · FDX · Fountain</span>
        </div>
      </div>
    ),
  },
  {
    icon: Sparkle,
    label: 'Step 3 · Style preset',
    title: 'Tune the proofreader to your genre',
    body:
      'Style presets keep literary fiction sounding literary, screenplays formatted correctly, and song lyrics respected as written. Choose from Pop, Rock, Folk, Literary, Commercial, and more.',
    mock: (
      <div className="space-y-3">
        <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">Style Preset</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {['Literary Fiction', 'Commercial Fiction', 'Screenplay · Drama', 'Song · Pop', 'Song · Folk', 'Memoir'].map((s) => (
            <span key={s} className="px-3 py-2 rounded-md border border-border bg-card text-bone">{s}</span>
          ))}
        </div>
      </div>
    ),
  },
  {
    icon: Lightning,
    label: 'Step 4 · Proofread',
    title: 'Click Proofread — watch it work',
    body:
      'For short pieces it’s a few seconds. For a 70 000-word novel, the chunked engine shows "Polishing chunk 3 of 14" so you always know how far through it is.',
    mock: (
      <div className="space-y-4">
        <div className="flex items-center justify-between text-xs">
          <span className="text-ash">Polishing chunk 3 of 5</span>
          <span className="text-gold tabular-nums">60%</span>
        </div>
        <FakeBar pct={60} />
        <button
          type="button"
          tabIndex={-1}
          className="mt-2 px-4 py-2 rounded-lg bg-gold text-charcoal text-[10px] font-medium uppercase tracking-[0.28em] inline-flex items-center gap-2"
        >
          Proofreading...
          <ArrowRight size={12} weight="bold" />
        </button>
      </div>
    ),
  },
  {
    icon: Eye,
    label: 'Step 5 · Side-by-side',
    title: 'Compare the change — accept or trust',
    body:
      'When it finishes, your original sits on the left and the corrected version on the right, with every change inline-highlighted. Strike-through marks what was removed; gold underline shows what was added.',
    mock: (
      <div className="grid grid-cols-2 gap-3 text-sm leading-relaxed">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-2">Original</p>
          <p className="text-bone italic font-serif text-xs">She walked <span className="bg-rose-400/15 line-through decoration-rose-400 px-0.5 rounded">quick</span> across the room.</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2">Corrected</p>
          <p className="text-ivory font-serif text-xs">She walked <span className="bg-emerald-400/15 underline decoration-gold underline-offset-2 px-0.5 rounded">quickly</span> across the room.</p>
        </div>
      </div>
    ),
  },
  {
    icon: Star,
    label: 'Step 6 · Quality rating',
    title: 'A score out of 100, with editor’s notes',
    body:
      'Below the comparison, a Quality Rating panel scores six craft categories — Story, Prose, Pacing, Dialogue, Character, Voice — out of 100, with a paragraph of constructive editorial feedback.',
    mock: (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.28em] text-gold">Quality Rating</span>
          <span className="font-serif text-2xl text-emerald-300 leading-none">87<span className="text-xs text-ash">/100</span></span>
        </div>
        {[
          ['Story', 90, 'bg-emerald-400'],
          ['Prose', 84, 'bg-gold'],
          ['Pacing', 78, 'bg-gold'],
          ['Voice', 88, 'bg-emerald-400'],
        ].map(([cat, n, color]) => (
          <div key={cat}>
            <div className="flex justify-between text-[11px] mb-1"><span className="text-bone">{cat}</span><span className="text-bone tabular-nums">{n}</span></div>
            <FakeBar pct={n} color={color} />
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: MusicNote,
    label: 'Songwriter mode',
    title: 'Lyrics preserved, analysis delivered',
    body:
      'For songs, the right pane simply confirms "Lyrics preserved — see analysis panel below". Underneath, a Songwriter Analysis panel breaks down theme, structure, tone, rhyme, standout lines, and what to develop.',
    mock: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-gold"><MusicNote size={14} weight="duotone" /> Lyrics · Preserved</div>
        <p className="font-serif italic text-sm text-ivory leading-relaxed">walkin down the road tonite<br/>nothing feels quiet rite</p>
        <div className="pt-2 border-t border-border/60" />
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Analysis</div>
        <ul className="space-y-1.5 text-[11px] text-bone leading-relaxed">
          <li><span className="text-gold mr-2 uppercase tracking-wide">Theme</span>Restless longing.</li>
          <li><span className="text-gold mr-2 uppercase tracking-wide">Voice</span>Demo-tape intimacy preserved.</li>
        </ul>
      </div>
    ),
  },
  {
    icon: Download,
    label: 'Step 7 · Download',
    title: 'Three clean export formats',
    body:
      'Download the corrected file in TXT, DOCX, or PDF (PDF is available for songwriter analysis, bundling lyrics + critique on a properly typeset page). Files are clean — no highlight markup.',
    mock: (
      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-[0.28em] text-ash">Export</p>
        <div className="flex flex-wrap gap-2">
          <span className="px-3 py-2 rounded-md border border-gold/40 bg-gold/10 text-gold text-xs">TXT</span>
          <span className="px-3 py-2 rounded-md border border-gold/40 bg-gold/10 text-gold text-xs">DOCX</span>
          <span className="px-3 py-2 rounded-md border border-gold/40 bg-gold/10 text-gold text-xs">PDF · Songs</span>
        </div>
        <p className="text-[11px] text-bone leading-relaxed pt-2">Your downloaded file is pristine — no markup, no comments, just the corrected text ready to send.</p>
      </div>
    ),
  },
  {
    icon: ClockClockwise,
    label: 'Step 8 · History',
    title: 'Every session, saved automatically',
    body:
      'Past proofreads appear in the History sidebar — re-open them with one click to view, re-download, or compare two sessions side-by-side. Nothing is lost.',
    mock: (
      <div className="space-y-2 text-xs">
        <p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-3">History</p>
        {[
          { t: 'Chapter 7 · revision', w: '4,200 words', d: 'Today' },
          { t: 'Song · Sunlit Window', w: 'Analysis', d: 'Yesterday' },
          { t: 'Pilot · Cold Open', w: '6,180 words', d: '2 days ago' },
        ].map((s) => (
          <div key={s.t} className="flex items-center justify-between border-b border-border/40 pb-2">
            <div>
              <p className="text-ivory">{s.t}</p>
              <p className="text-[10px] text-ash">{s.w} · {s.d}</p>
            </div>
            <span className="text-[10px] text-gold uppercase tracking-wider">Open</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Quotes,
    label: 'You’re ready',
    title: 'Start with 3,000 free words',
    body:
      'No card required. Run a chapter, a scene, or a song through the system — see what it catches before you commit. Click Start free below to create your account in thirty seconds.',
    mock: (
      <div className="flex flex-col items-center justify-center h-full text-center py-6">
        <Quotes size={28} weight="duotone" className="text-gold mb-3" />
        <p className="font-serif italic text-ivory text-base leading-relaxed max-w-[260px]">
          “Felt like having a careful editor read the whole book in an afternoon.”
        </p>
      </div>
    ),
  },
];

export const WelcomeTour = ({ open, onClose, onPrimaryCta }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) setStep(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setStep((s) => Math.min(s + 1, STEPS.length - 1));
      else if (e.key === 'ArrowLeft') setStep((s) => Math.max(s - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] bg-card border-border text-ivory"
        data-testid="welcome-tour-modal"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-serif text-xl tracking-tight flex items-center gap-3">
            <PlayCircle size={20} weight="duotone" className="text-gold" />
            How to use Manuscript Polish
            <span className="ml-auto text-[10px] font-mono text-ash tabular-nums">
              {step + 1} / {STEPS.length}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-4 shrink-0" data-testid="welcome-tour-dots">
          {STEPS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'bg-gold w-8' : 'bg-stone w-3 hover:bg-bone/50'
              }`}
              data-testid={`tour-dot-${i}`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto pr-1" data-testid={`tour-step-${step}`}>
          <Step {...current} />
        </div>

        {/* Footer controls */}
        <div className="flex items-center justify-between gap-3 shrink-0 pt-4 border-t border-border/60">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(s - 1, 0))}
            disabled={isFirst}
            className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold px-3 py-2 rounded-md hover:bg-stone disabled:opacity-30 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
            data-testid="tour-prev"
          >
            <CaretLeft size={12} weight="bold" />
            Previous
          </button>

          {isLast ? (
            <button
              type="button"
              onClick={() => { onClose(); onPrimaryCta?.(); }}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark transition-colors inline-flex items-center gap-2"
              data-testid="tour-cta-finish"
            >
              Start free
              <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(s + 1, STEPS.length - 1))}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark transition-colors inline-flex items-center gap-2"
              data-testid="tour-next"
            >
              Next
              <CaretRight size={14} weight="bold" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
